































































































import { PropType } from 'vue';
import { computed, defineComponent, nextTick, ref, toRefs, watch } from '@vue/composition-api';
import {
  AdgroupConfig,
  AdgroupFormData,
  AreaItem,
  CampaignFormData,
  CreateMode,
  FORM_ITEM_ELEMENT_ID,
} from '@tt4b/ttam-enums';
import ALink from '@/components/common/a-link/a-link.vue';
import { uniqBy, difference, uniq, isEqual } from 'lodash-es';
import { trackEvent } from './../../../utils/index';
import Location from './index';
import { useStdAdgroupIs, useAdgroupLocationAvailable, useAdgroupMode, useSharedAreaStore } from '@tt4b/ttam-hooks';
import { UploadFileSupportConfig, MAX_COUNT } from './const';
import { Message } from 'byted-vui';
import { i18n } from '@tt4b/ttam-i18n';

const name = 'ttam-location-compose';
export default defineComponent({
  name,
  components: {
    TtamLocation: Location.TtamLocation,
    TtamLocationTips: Location.TtamLocationTips,
    TtamLocationSelectedBox: Location.TtamLocationSelectedBox,
    TtamLocationBulkUpload: Location.TtamLocationBulkUpload,
    TtamLocationUndoCurrentChanges: Location.TtamLocationUndoCurrentChanges,
    ALink,
  },
  props: {
    campaignData: {
      type: Object as PropType<CampaignFormData>,
      default: () => ({}),
    },
    adgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    originAdgroupData: {
      type: Object as PropType<AdgroupFormData>,
      default: () => ({}),
    },
    adgroupConfig: {
      type: Object as PropType<AdgroupConfig>,
      default: () => ({}),
    },
    // 自定义过滤函数用于过滤可选的国家
    // eslint-disable-next-line vue/require-default-prop
    filterMethod: {
      type: Function as PropType<(param: AreaItem[]) => AreaItem[]>,
    },
    trackMethod: {
      type: Function as PropType<() => void>,
    },
    createMode: {
      type: String as PropType<CreateMode>,
      default: CreateMode.CREATE,
    },
    // 错误字段map，初始化后的报错信息
    errorsMap: {
      type: Object as PropType<Record<string, string>>,
      default: () => ({}),
    },
    noDataLabel: {
      type: String,
    },
    noDataBtn: {
      type: String,
    },
    isBsEditable: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, { emit }) {
    const { adgroupData, originAdgroupData, campaignData, adgroupConfig, createMode, filterMethod, isBsEditable } =
      toRefs(props);
    const { locationValidator } = Location.useRules({ adgroupData, originAdgroupData, campaignData, createMode });
    const helpHref = ref('/help/article?aid=6669234451085524998');
    const particleLocations = ref();

    const { locationSearchResultList, handleLogSearchResult } = Location.useLocationSearchResultList();

    const isZipcodeTips = computed(() => adgroupData.value.zipcodeIds?.length > 0);

    const {
      setLastTimeSelected,
      setManualSelected,
      updateManualSelected,
      addedLocations,
      deletedLocations,
      zipcodeIdsSnapShot,
      particleLocationsSnapShot,
      visibleCurrentChangeTip,
    } = Location.useUndoCurrentChanges();

    if (typeof props.trackMethod === 'function') {
      props.trackMethod();
    }

    function handleUndoCurrentChanges() {
      emit('on-change', {
        particleLocations: particleLocationsSnapShot.value,
        zipcodeIds: zipcodeIdsSnapShot.value,
      });
    }

    watch(
      () => [adgroupData.value.particleLocations, adgroupData.value.zipcodeIds],
      (newVal, oldVal) => {
        if (isEqual(newVal, oldVal)) {
          return;
        }
        setLastTimeSelected({
          particleLocations: oldVal[0],
          zipcodeIds: oldVal[1],
        });
        updateManualSelected({
          particleLocations: newVal[0],
          zipcodeIds: newVal[1],
        });

        nextTick(() => {
          particleLocations.value?.validate?.('custom');
        });
      },
    );

    const _selection = computed(() => [...adgroupData.value.particleLocations, ...adgroupData.value.zipcodeIds]);

    const { offlineAreaTipsVisible, offlineAreaTips } = Location.usePropsOfflineAreaTipsVisible({
      campaignData,
      adgroupData,
      originAdgroupData,
      createMode,
    });

    const { DSALoading, DSAInfo, openDSAClick } = Location.useDSA();
    const { japanVideoTipsVisible } = Location.usePropsJapanVideoTipsVisible({ adgroupData });
    const { dsaTipsVisible } = Location.usePropsDSATipsVisible({ adgroupData, DSAInfo });

    const { treeProps, treeResultListType, filterCheckStrictly, selectTreeFilterMethod, enableSelectOneNode } =
      Location.useTreeProps({ adgroupData });
    const { brandSafetyTipVisible, brandSafetyTipContent } = Location.usePropsBrandSafetyTipVisible({
      campaignData,
      adgroupData,
      createMode,
      isBsEditable,
    });
    const { anchorTipContent } = Location.useAnchorTipsVisibly(adgroupData, createMode);
    const { disabledLocation } = Location.usePropsDisabledLocation({ adgroupData, adgroupConfig });

    const { isNewLocationAvailable } = useAdgroupLocationAvailable();
    const {
      availableAreaList,
      virtualNodeIdArr,
      virtualNodeIdParentIdMap,
      metroIdsMap,
      regionIdsMap,
      isLoading: isGettingInitData,
      data,
    } = Location.useDataSource({ adgroupData, campaignData, filterMethod });
    const { getAreaMapInArea } = useSharedAreaStore();
    const { resetLocationData, areaMap, updateOfflineAreaTipsVisibleState } = Location.useLocationTargetingDataSource();
    const { handleConflictDetection, handleVirtualNode, handleHierarchicalRelationship } =
      Location.useLocationChildrenMerger({
        areaMap,
      });
    const {
      onSearchKeywordChange,
      subscribeFuzzySearch,
      isLoading: isSearching,
      searchKeyword,
    } = Location.useLocationSearchResultDataSource({
      adgroupData,
      campaignData,
      availableAreaList,
    });

    const _selectionTreeData = computed(() => {
      const v = adgroupData.value.particleLocations.filter(id => getAreaMapInArea()[id]);

      return v;
    });

    const disabledBulkUpload = computed(() => !adgroupData.value.inventoryFlow.length || disabledLocation.value);

    const treeData = computed(() => {
      if (!isNewLocationAvailable.value) {
        return availableAreaList.value;
      }

      if (searchKeyword.value) {
        if (locationSearchResultList.value.length) {
          const selectionAreaList = _selectionTreeData.value.map(id =>
            Object.assign({}, areaMap.value[id] ?? {}, { visible: false, filterable: false, children: [] }),
          );
          return uniqBy([...locationSearchResultList.value, ...selectionAreaList], 'id');
        } else {
          return [];
        }
      } else {
        return availableAreaList.value;
      }
    });

    function onLimitSelect() {
      Message.warning(`${i18n.t('adgroup_location_tip_select_number_limit', { num: MAX_COUNT })}`);
    }

    const handleChangeLocation = (result: { particleLocations: number[]; zipcodeIds?: number[] }) => {
      emit('on-change', result);
      updateOfflineAreaTipsVisibleState(false);
    };

    /** 处理虚拟节点 */
    const onLocationChange = (val: number[] = [], nodes: AreaItem[], node: AreaItem, isChecked: boolean) => {
      let checkedList = handleVirtualNode({
        node,
        nodeIds: val,
        isChecked,
        virtualNodeIdArr,
        virtualNodeIdParentIdMap,
        metroIdsMap,
        regionIdsMap,
      });

      if (isNewLocationAvailable.value) {
        handleLogSearchResult(nodes);

        const allCheckedList = checkedList;
        // 非搜索时，取消勾选
        const isDelete = !searchKeyword.value && !isChecked;

        // 删除场景：
        // newIds = （现状所有已选ids - 本次删除的ids + select tree中剩余选中的）去重
        // 本次删除的ids = 现状select tree中已选的ids - select tree中剩余选中的
        const { zipcodeIdsAfterResolveConflict, particleLocationsAfterResolveConflict } = handleConflictDetection({
          newIds: isDelete
            ? uniq([
                ...difference(_selection.value, difference(_selectionTreeData.value, allCheckedList)),
                ...allCheckedList,
              ])
            : difference(allCheckedList, _selection.value),
          preIds: _selection.value,
          deletedIds: difference(_selectionTreeData.value, allCheckedList),
        });
        handleChangeLocation({
          zipcodeIds: zipcodeIdsAfterResolveConflict,
          particleLocations: particleLocationsAfterResolveConflict,
        });
        setManualSelected({
          zipcodeIds: zipcodeIdsAfterResolveConflict,
          particleLocations: particleLocationsAfterResolveConflict,
        });
      } else {
        handleChangeLocation({
          particleLocations: checkedList,
        });
      }
    };

    const iconHoverTrack = () => trackEvent('create_adgroup_location_tips_hover');

    const clearAllTrack = () => trackEvent('create_adgroup_location_clear_all');

    const { isCLA } = useStdAdgroupIs({
      adgroupFormData: adgroupData,
      campaignFormData: campaignData,
    });

    const isLoading = computed(() => isGettingInitData.value || isSearching.value);

    if (isNewLocationAvailable.value) {
      subscribeFuzzySearch();
    }

    // 新版回显框
    const { selectedList, handleSingleTagDelete } = Location.useLocationSelectedList({
      adgroupData,
    });

    const onSingleTagDelete = val => {
      const { particleLocationsSet, zipcodeIdsSet } = handleSingleTagDelete(val);
      handleChangeLocation({
        particleLocations: Array.from(particleLocationsSet),
        zipcodeIds: Array.from(zipcodeIdsSet),
      });
    };

    const onClearAll = () => {
      resetLocationData();
      handleChangeLocation({
        particleLocations: [],
        zipcodeIds: [],
      });
    };

    // 批量上传
    const { checkLocation, invalidLocationList, notSupportedLocationList, notMatchedLocationList, resetErrorList } =
      Location.useBulkUploadValidate({ adgroupData, campaignData });

    const { handleLogUploadResult, countryListMap, locationTypeList } = Location.useLocationBulkUploadDataSource({
      adgroupData,
      campaignData,
      availableAreaList,
    });

    const { isSupportBulkUpload, bulkUploadEnabledMaxNumber } = Location.usePropsSupportBulkUpload({
      adgroupData,
      campaignData,
      availableAreaList,
    });

    const handleAddBulkUploadLocation = bulkData => {
      handleLogUploadResult(bulkData.locations);

      const ids = bulkData.locations.map(location => location?.info?.geo?.id);
      const idsAfterHandleHierarchicalRelationship = handleHierarchicalRelationship(ids);
      const { zipcodeIdsAfterResolveConflict, particleLocationsAfterResolveConflict } = handleConflictDetection({
        newIds: idsAfterHandleHierarchicalRelationship,
        preIds: _selection.value,
      });
      handleChangeLocation({
        zipcodeIds: zipcodeIdsAfterResolveConflict,
        particleLocations: particleLocationsAfterResolveConflict,
      });
      setManualSelected({
        zipcodeIds: zipcodeIdsAfterResolveConflict,
        particleLocations: particleLocationsAfterResolveConflict,
      });
    };

    return {
      name,
      locationValidator,
      helpHref,
      _selection,
      isZipcodeTips,
      _selectionTreeData,
      particleLocations,
      offlineAreaTipsVisible,
      offlineAreaTips,
      DSALoading,
      japanVideoTipsVisible,
      dsaTipsVisible,
      isLoading,
      disabledLocation,
      disabledBulkUpload,
      treeData,
      visibleCurrentChangeTip,
      treeProps,
      data,
      automatedTargeting: computed(() => adgroupData.value?.automatedTargeting),
      adgroupId: computed(() => adgroupData.value?.adId || ''),
      openDSAClick,
      onLocationChange,
      iconHoverTrack,
      clearAllTrack,
      isCLA,
      treeResultListType,
      filterCheckStrictly,
      selectTreeFilterMethod,
      selectedList,
      onSingleTagDelete,
      onClearAll,
      handleAddBulkUploadLocation,
      checkLocation,
      invalidLocationList,
      notSupportedLocationList,
      notMatchedLocationList,
      resetErrorList,
      onSearchKeywordChange,
      deletedLocations,
      addedLocations,
      handleUndoCurrentChanges,
      isNewLocationAvailable,
      countryListMap,
      locationTypeList,
      isSupportBulkUpload,
      brandSafetyTipVisible,
      brandSafetyTipContent,
      UploadFileSupportConfig,
      bulkUploadEnabledMaxNumber,
      enableSelectOneNode,
      onLimitSelect,
      anchorTipContent,
      FORM_ITEM_ELEMENT_ID,
    };
  },
});
